import React, {useEffect, useLayoutEffect, useRef, useState} from 'react';

// Keep only nearby content mounted, with a measured placeholder and focus pin.
export default function DeferredSongBody({defer = false, estimatedHeight = 20, anchorNumber, layoutKey, children}) {
  const ref = useRef(null);
  const placeholder = useRef(null);
  const [mounted, setMounted] = useState(() => !defer || typeof IntersectionObserver === 'undefined');
  const [height, setHeight] = useState(null);
  const [focused, setFocused] = useState(false);
  const visible = mounted || focused || !defer || typeof IntersectionObserver === 'undefined';
  useEffect(() => {
    if (!defer || typeof IntersectionObserver === 'undefined') return;
    const target = ref.current;
    const observer = new IntersectionObserver(entries => {
      const next = entries.some(entry => entry.isIntersecting);
      if (next && target.dataset.deferred) placeholder.current = target.getBoundingClientRect();
      setMounted(next);
    }, {root: target.closest('.song-view'), rootMargin: '720px 0px'});
    observer.observe(target);
    return () => observer.disconnect();
  }, [defer]);
  useLayoutEffect(() => {
    if (!visible || !placeholder.current) return;
    const previous = placeholder.current; placeholder.current = null;
    const target = ref.current, root = target.closest('.song-view');
    const measured = target.getBoundingClientRect().height;
    // Nearby rows mounted above the scrollport must not push the viewed song
    // away when their estimate is replaced by the actual wrapped mobile text.
    if (root && previous.bottom <= root.getBoundingClientRect().top) root.scrollTop += measured - previous.height;
    if (measured > 0) setHeight(measured);
  }, [visible]);
  useEffect(() => {
    if (!visible || typeof ResizeObserver === 'undefined') return;
    const target = ref.current;
    const measure = () => {const value = target.getBoundingClientRect().height; if (value > 0) setHeight(value);};
    const observer = new ResizeObserver(measure); observer.observe(target); measure();
    return () => observer.disconnect();
  }, [visible]);
  useEffect(() => setHeight(null), [layoutKey, estimatedHeight]);
  return <div ref={ref} id={!visible && anchorNumber ? `song-${anchorNumber}` : undefined} className="deferred-song-body" data-deferred={visible ? undefined : 'true'}
    onFocusCapture={() => setFocused(true)}
    onBlurCapture={event => {if (!event.currentTarget.contains(event.relatedTarget)) setFocused(false);}}
    style={visible ? undefined : {minHeight: height == null ? `${estimatedHeight}em` : `${height}px`}}>
    {visible ? children : anchorNumber ? <span id={`song-title-${anchorNumber}`} /> : null}
  </div>;
}
